import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, ShieldCheck, Send, Loader2, CheckCircle2 } from "lucide-react";
import SEO from "../components/site/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "SOC Level 2 Monitoring",
  "VAPT (Vulnerability Assessment & Pen Testing)",
  "Threat Detection & Response",
  "Network Device Monitoring",
  "End-to-End Security Monitoring",
  "Compliance & Audit Support",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setSubmitted(true);
      toast.success("Request received. A Quasect analyst will reach out within 24 hours.");
      setForm({ name: "", email: "", company: "", phone: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Couldn't submit your request. Please try again or email info@quasect.com.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#050507] text-white">
      <SEO
        title="Contact Quasect — Secure Your Business Today"
        description="Talk to a Quasect L2 SOC analyst about VAPT, threat protection, and 24/7 security monitoring. Email info@quasect.com."
        path="/contact"
      />

      <section className="relative overflow-hidden noise border-b border-white/10">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 pb-12 md:pt-32 md:pb-16">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#39FF14] flex items-center gap-3">
            <span className="w-6 h-px bg-[#39FF14]" /> CONTACT / SOC INTAKE
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="contact-headline"
            className="font-display font-black mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-[72px] tracking-tighter leading-[1.02] max-w-5xl break-words"
          >
            Secure your business <span className="text-[#39FF14]">today.</span>
          </motion.h1>
          <p className="mt-6 text-lg md:text-xl text-[#8B93A5] max-w-3xl leading-relaxed">
            Talk to a Quasect L2 analyst within 24 hours. Tell us about your environment and
            we'll come back with a scoped engagement plan.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          {/* Info side */}
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-white/10 bg-[#0A0A0D] p-7 noise relative">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] mb-4">
                Direct Contact
              </div>
              <a
                href="mailto:info@quasect.com"
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-xl md:text-2xl font-display font-bold hover:text-[#39FF14] transition-colors"
              >
                <Mail className="w-5 h-5 text-[#39FF14]" />
                info@quasect.com
              </a>
              <p className="mt-4 text-[#8B93A5] leading-relaxed">
                For active incidents, mark your subject line with{" "}
                <span className="font-mono text-[#39FF14]">[INCIDENT]</span>. Routed directly to
                on-call SOC lead.
              </p>
            </div>

            <div className="border border-white/10 bg-[#0A0A0D] p-7">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] mb-4">
                Response SLA
              </div>
              <ul className="space-y-3 text-[#8B93A5]">
                <li className="flex justify-between gap-4 border-b border-white/10 pb-3">
                  <span>General inquiry</span>
                  <span className="font-mono text-white">{"< 24h"}</span>
                </li>
                <li className="flex justify-between gap-4 border-b border-white/10 pb-3">
                  <span>Demo request</span>
                  <span className="font-mono text-white">{"< 12h"}</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>Active incident</span>
                  <span className="font-mono text-[#39FF14]">{"< 1h"}</span>
                </li>
              </ul>
            </div>

            <div className="border border-white/10 bg-[#0A0A0D] p-7 flex items-start gap-4">
              <ShieldCheck className="w-6 h-6 text-[#00F0FF] shrink-0 mt-1" />
              <div>
                <h3 className="font-display font-bold text-lg">NDA-First Conversations</h3>
                <p className="text-[#8B93A5] mt-2 leading-relaxed">
                  Mutual NDA available before any environment details are shared. No sales-led
                  intake — every first call is technical.
                </p>
              </div>
            </div>
          </div>

          {/* Form side */}
          <div className="lg:col-span-7">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="contact-success"
                className="border border-[#39FF14]/40 bg-[#0A0A0D] p-10 md:p-14 text-center noise relative"
              >
                <div className="w-16 h-16 mx-auto border border-[#39FF14]/50 flex items-center justify-center bg-[#39FF14]/10">
                  <CheckCircle2 className="w-7 h-7 text-[#39FF14]" />
                </div>
                <h2 className="font-display font-bold text-3xl md:text-4xl mt-7 tracking-tight">
                  Request received.
                </h2>
                <p className="mt-4 text-[#8B93A5] max-w-md mx-auto leading-relaxed">
                  A Quasect SOC lead will reach out within 24 hours. For active incidents, email{" "}
                  <a className="text-[#39FF14]" href="mailto:info@quasect.com">
                    info@quasect.com
                  </a>{" "}
                  with subject <span className="font-mono">[INCIDENT]</span>.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-reset"
                  className="mt-8 border border-white/20 hover:border-white text-white px-6 py-3 text-sm uppercase tracking-wider transition-colors"
                >
                  Submit another request
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="border border-white/10 bg-[#0A0A0D] p-7 md:p-10 space-y-5 noise relative"
              >
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Full Name *" testid="contact-name">
                    <input
                      type="text"
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={handle("name")}
                      required
                      className={inputCls}
                      placeholder="Jane Doe"
                    />
                  </Field>
                  <Field label="Work Email *" testid="contact-email">
                    <input
                      type="email"
                      data-testid="contact-input-email"
                      value={form.email}
                      onChange={handle("email")}
                      required
                      className={inputCls}
                      placeholder="jane@company.com"
                    />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Company">
                    <input
                      type="text"
                      data-testid="contact-input-company"
                      value={form.company}
                      onChange={handle("company")}
                      className={inputCls}
                      placeholder="Acme Corp"
                    />
                  </Field>
                  <Field label="Phone">
                    <input
                      type="tel"
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={handle("phone")}
                      className={inputCls}
                      placeholder="+1 555 123 4567"
                    />
                  </Field>
                </div>
                <Field label="Service of Interest">
                  <select
                    data-testid="contact-input-service"
                    value={form.service}
                    onChange={handle("service")}
                    className={`${inputCls} appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%2300F0FF%22 stroke-width=%222%22><polyline points=%226 9 12 15 18 9%22/></svg>')] bg-no-repeat bg-[right_1rem_center]`}
                  >
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s} className="bg-[#0A0A0D]">
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="How can we help? *">
                  <textarea
                    rows={5}
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={handle("message")}
                    required
                    className={`${inputCls} resize-none`}
                    placeholder="Tell us about your environment, current tooling, and what you'd like to improve…"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-sweep w-full md:w-auto bg-[#39FF14] text-black font-bold uppercase tracking-wider px-8 py-4 text-sm hover:bg-[#32E612] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Secure Your Business <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
                <p className="text-xs text-[#8B93A5] font-mono uppercase tracking-wider">
                  By submitting, you consent to be contacted by Quasect's security team.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-black border border-white/15 text-white placeholder:text-[#5d6373] px-4 py-3.5 outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-colors font-mono text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B93A5] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
